import { Box, Icon, Tooltip } from '@chakra-ui/react';
import { LinkExternal02 } from '@kula-ai/icons/dual-tone/LinkExternal02';
import { Avatar } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import type { UnderwayActivity } from './types';
import {
  getActivityCopyConfig,
  type ActivityExtraClause,
} from './activityCopy';
import { getCandidateApplicationUrl } from './candidateApplicationUrl';

// Interpolated tag values (candidate / stage / job / activity / recipient
// names) are capped so the whole sentence stays within ~3 lines. A value longer
// than this shows an ellipsis; the full value stays available on hover (tooltip)
// and to screen readers (aria-label).
const MAX_TAG_LENGTH = 20;

// Renders a tag value inline, truncating with an ellipsis + tooltip past
// MAX_TAG_LENGTH. Kept as an inline `<Box as="span">` (not `@/ui/TooltipText`,
// which renders a Chakra `Text` that imposes its own font/block flow) so it
// flows within the chip and inherits the sentence's font. Chakra `Tooltip` (not
// kula-ai's compound Trigger/Content API) matches the existing `TooltipText`
// util and needs no wrapper for a single inline word.
const TagText = ({ value }: { value: string }) => {
  if (!value) return null;
  if (value.length <= MAX_TAG_LENGTH) {
    return <Box as="span">{value}</Box>;
  }
  return (
    <Tooltip label={value} placement="top" borderRadius="6px" hasArrow>
      <Box as="span" aria-label={value}>
        {`${value.slice(0, MAX_TAG_LENGTH)}…`}
      </Box>
    </Tooltip>
  );
};

type UnderwayItemDescriptionProps = {
  activity: UnderwayActivity;
  // Activity's own name (stage suffix already stripped by the caller).
  activityName: string;
  // Opens the "N selected candidates" modal from the bulk count chip.
  onOpenCandidates: () => void;
};

// Neutral (gray) chip — the non-interactive nouns: activity name, stage, job.
const GrayChip = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    bg="secondary-50"
    borderRadius="6px"
    px="4px"
    py="0px"
    color="secondary-800"
    verticalAlign="middle"
    lineHeight="1.4"
  >
    {children}
  </Box>
);

// Primary (purple) chip — the "who/what this is for" values: candidate,
// count, recipient, provider/schedule mode. Renders as a link when `href`
// is set, a button when `onClick` is set, else a plain span.
const PurpleChip = ({
  children,
  href,
  onClick,
  ariaLabel,
  testId,
}: {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  ariaLabel?: string;
  testId?: string;
}) => {
  const interactive = Boolean(href || onClick);
  return (
    <Box
      {...(href
        ? { as: 'a', href, target: '_blank', rel: 'noopener noreferrer' }
        : onClick
          ? { as: 'button', type: 'button', onClick }
          : { as: 'span' })}
      display="inline-flex"
      alignItems="center"
      gap="6px"
      bg="primary-50"
      color="primary-700"
      borderRadius="6px"
      px="4px"
      py="0px"
      border="none"
      verticalAlign="middle"
      lineHeight="1.4"
      textDecoration="none"
      cursor={interactive ? 'pointer' : undefined}
      _hover={interactive ? { bg: 'primary-100' } : undefined}
      aria-label={ariaLabel}
      data-test-id={testId}
    >
      {children}
    </Box>
  );
};

const pluralNoun = (noun: 'reviewer' | 'user', count: number): string =>
  count === 1 ? noun : `${noun}s`;

export const UnderwayItemDescription = ({
  activity,
  activityName,
  onOpenCandidates,
}: UnderwayItemDescriptionProps) => {
  const { verb, subjectPreposition, extra } = getActivityCopyConfig(activity);
  const candidateCount = activity.candidates.length;
  const firstCandidate = activity.candidates[0];

  // Bulk → clickable count opening the candidate list; multi (non-bulk) →
  // plain count; single → the candidate chip with avatar + name.
  const renderSubject = () => {
    if (activity.isBulk) {
      return (
        <>
          <PurpleChip
            onClick={onOpenCandidates}
            ariaLabel={`View ${candidateCount} selected candidates`}
            testId="automation-candidate-count"
          >
            {candidateCount}
          </PurpleChip>{' '}
          candidates
        </>
      );
    }
    if (candidateCount > 1) {
      return (
        <>
          <PurpleChip>{candidateCount}</PurpleChip> candidates
        </>
      );
    }
    return (
      <PurpleChip
        href={
          getCandidateApplicationUrl(activity.jobId, firstCandidate?.id) ||
          undefined
        }
      >
        <Avatar
          size="3xs"
          name={firstCandidate?.name}
          src={firstCandidate?.avatarUrl}
        />
        <TagText value={firstCandidate?.name ?? 'candidate'} />
      </PurpleChip>
    );
  };

  const renderExtra = (clause: ActivityExtraClause) => {
    if (!clause) return null;

    if (clause.type === 'using') {
      return (
        <>
          {' '}
          using{' '}
          <PurpleChip>
            <Icon as={clause.icon} w="14px" h="14px" />
            <TagText value={clause.label} />
          </PurpleChip>
        </>
      );
    }

    // recipient: reviewers (review) or users (employee survey).
    const recipients = activity.details?.recipients ?? [];
    if (recipients.length === 0) return null;
    return (
      <>
        {' '}
        to{' '}
        {recipients.length === 1 ? (
          <PurpleChip>
            <Avatar
              size="3xs"
              name={recipients[0]?.name}
              src={recipients[0]?.avatarUrl}
            />
            <TagText value={recipients[0]?.name ?? ''} />
          </PurpleChip>
        ) : (
          <>
            <PurpleChip>{recipients.length}</PurpleChip>{' '}
            {pluralNoun(clause.noun, recipients.length)}
          </>
        )}
      </>
    );
  };

  return (
    <KText size="sm-regular" color="secondary-700" mt="8px" lineHeight="2">
      {verb}{' '}
      <GrayChip>
        <TagText value={activityName} />
      </GrayChip>{' '}
      {subjectPreposition} {renderSubject()}
      {renderExtra(extra)} in the{' '}
      <GrayChip>
        <TagText value={activity.stageName} />
      </GrayChip>{' '}
      stage
      {/* Job clause only when we have a name — some move surfaces (e.g. the
          job's own candidate list) don't carry the job on the row. */}
      {activity.jobName ? (
        <>
          {' '}
          of{' '}
          {activity.jobId ? (
            <Box
              as="a"
              href={`/jobs/${activity.jobId}`}
              target="_blank"
              rel="noopener noreferrer"
              display="inline-flex"
              alignItems="center"
              gap="4px"
              bg="secondary-50"
              color="secondary-800"
              borderRadius="6px"
              px="4px"
              py="0px"
              verticalAlign="middle"
              lineHeight="1.4"
              textDecoration="none"
              _hover={{ bg: 'secondary-100' }}
            >
              <TagText value={activity.jobName} />
              <Icon
                as={LinkExternal02}
                w="12px"
                h="12px"
                color="secondary-700"
              />
            </Box>
          ) : (
            <GrayChip>
              <TagText value={activity.jobName} />
            </GrayChip>
          )}{' '}
          job
        </>
      ) : null}
    </KText>
  );
};
